/**
 * AskAweniFab — floating action button + bottom sheet of one-tap prompts.
 *
 * Goal: replace the "I'd open ChatGPT" reflex with an in-house equivalent
 * that requires zero typing for the 8 most common conversational
 * intents. Users still keep the option to type, but the FAB never opens
 * a blank text box — it opens a smart preset sheet first.
 *
 * Behavior:
 *   - Always-visible FAB on the home page (anchored bottom-end above the
 *     bottom nav bar). Hidden on scroll-up reveal once the user is
 *     reading deep content (so it doesn't cover article text).
 *   - Tap: opens a bottom sheet of 8 contextual preset prompts. The
 *     prompts are rotated by AnticipationEngine signals (time of day,
 *     country, mode) so the sheet always feels relevant.
 *   - Each preset is a one-tap button → navigates to ChatModule with the
 *     prompt pre-injected and dispatched.
 *   - "Custom message" appears at the bottom of the sheet for users who
 *     want to type — keyboard-first users still happy.
 *
 * Privacy: nothing is recorded outside ChatModule's existing storage.
 * The FAB itself is stateless; it only forwards intent.
 */

import type { AweniContext } from '../../types';
import type { LocationState } from './types';

interface AskAweniOptions {
  ctx: AweniContext;
  /** The home container — used to anchor the FAB inside the home page only. */
  hostContainer: HTMLElement;
  /** Provider of the live location for contextual presets. */
  getLocation: () => LocationState;
}

interface PresetPrompt {
  id: string;
  icon: string;
  /** i18n key for the BUTTON label (short). */
  labelKey: string;
  /** i18n key for the actual prompt sent to the chat. */
  promptKey: string;
  /** Optional contextual filter — when present, prompt is only shown if it returns true. */
  showIf?: (ctx: { hour: number; isFriday: boolean; isTraveling: boolean }) => boolean;
}

const ALL_PRESETS: PresetPrompt[] = [
  { id: 'plan_day', icon: '🗓', labelKey: 'ask_preset_plan_day', promptKey: 'ask_prompt_plan_day',
    showIf: ({ hour }) => hour >= 6 && hour < 11 },
  { id: 'recap_day', icon: '🌙', labelKey: 'ask_preset_recap_day', promptKey: 'ask_prompt_recap_day',
    showIf: ({ hour }) => hour >= 19 || hour < 4 },
  { id: 'find_food', icon: '🍽', labelKey: 'ask_preset_find_food', promptKey: 'ask_prompt_find_food',
    showIf: ({ hour }) => (hour >= 11 && hour < 14) || (hour >= 18 && hour < 21) },
  { id: 'jumuah', icon: '🕌', labelKey: 'ask_preset_jumuah', promptKey: 'ask_prompt_jumuah',
    showIf: ({ isFriday }) => isFriday },
  { id: 'translate_help', icon: '🌐', labelKey: 'ask_preset_translate_help', promptKey: 'ask_prompt_translate_help',
    showIf: ({ isTraveling }) => isTraveling },
  { id: 'budget_check', icon: '💰', labelKey: 'ask_preset_budget_check', promptKey: 'ask_prompt_budget_check' },
  { id: 'kids_activity', icon: '🧒', labelKey: 'ask_preset_kids_activity', promptKey: 'ask_prompt_kids_activity' },
  { id: 'quran_today', icon: '📖', labelKey: 'ask_preset_quran_today', promptKey: 'ask_prompt_quran_today' },
  { id: 'duas_now', icon: '🤲', labelKey: 'ask_preset_duas_now', promptKey: 'ask_prompt_duas_now' },
  { id: 'workout_5min', icon: '💪', labelKey: 'ask_preset_workout', promptKey: 'ask_prompt_workout' },
  { id: 'recipe_simple', icon: '🥗', labelKey: 'ask_preset_recipe', promptKey: 'ask_prompt_recipe' },
  { id: 'mosque_near', icon: '📍', labelKey: 'ask_preset_mosque_near', promptKey: 'ask_prompt_mosque_near' },
];

const STYLE_ID = 'aweni-askfab-styles';

function injectStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    .askfab-btn {
      position: fixed;
      bottom: 88px;
      inset-inline-end: 16px;
      width: 56px;
      height: 56px;
      border-radius: 999px;
      background: var(--ln-accent, #0A0A0A);
      color: var(--ln-accent-text, #fff);
      border: none;
      box-shadow: 0 6px 16px rgba(0,0,0,0.18);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 22px;
      z-index: 35;
      transition: transform var(--motion-duration-fast) var(--motion-easing-default), opacity var(--motion-duration-fast) var(--motion-easing-default);
    }
    .askfab-btn:hover { transform: translateY(-1px); }
    .askfab-btn:active { transform: scale(0.95); }
    .askfab-btn[hidden] { display: none; }
    .askfab-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,0.45);
      z-index: 50;
      animation: askfabOverlayIn var(--motion-duration-base) var(--motion-easing-default);
    }
    @keyframes askfabOverlayIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    .askfab-sheet {
      position: fixed;
      inset-inline: 0;
      bottom: 0;
      max-height: 80vh;
      overflow-y: auto;
      background: var(--ln-bg-card, #fff);
      border-top-left-radius: 20px;
      border-top-right-radius: 20px;
      padding: 16px 16px calc(16px + env(safe-area-inset-bottom));
      z-index: 51;
      animation: askfabSheetIn var(--motion-duration-base) var(--motion-easing-emphasized);
      box-shadow: 0 -8px 24px rgba(0,0,0,0.18);
    }
    @keyframes askfabSheetIn {
      from { transform: translateY(100%); }
      to   { transform: translateY(0); }
    }
    .askfab-grab {
      width: 44px;
      height: 4px;
      border-radius: 999px;
      background: rgba(0,0,0,0.15);
      margin: 0 auto 12px;
    }
    .askfab-title {
      font-size: 16px;
      font-weight: 800;
      color: var(--ln-text-primary, #1E293B);
      margin-bottom: 4px;
    }
    .askfab-sub {
      font-size: 12px;
      color: var(--ln-text-secondary, #6b6657);
      margin-bottom: 12px;
    }
    .askfab-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
      margin-bottom: 12px;
    }
    @media (min-width: 600px) {
      .askfab-grid { grid-template-columns: repeat(3, 1fr); }
    }
    .askfab-tile {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px;
      min-height: 56px;
      border-radius: 14px;
      border: 1px solid var(--ln-border, rgba(0,0,0,0.06));
      background: var(--ln-bg-card, #fff);
      cursor: pointer;
      text-align: start;
      font: inherit;
      color: inherit;
      font-size: 13px;
      font-weight: 600;
      line-height: 1.3;
      transition: transform var(--motion-duration-fast) var(--motion-easing-default), border-color var(--motion-duration-fast) var(--motion-easing-default);
    }
    .askfab-tile:hover { border-color: var(--ln-accent, #0A0A0A); }
    .askfab-tile:active { transform: scale(0.98); }
    .askfab-tile-icon { font-size: 20px; flex-shrink: 0; }
    .askfab-custom {
      display: flex;
      gap: 8px;
      align-items: center;
      padding: 12px;
      border-radius: 14px;
      background: var(--ln-bg-secondary, rgba(0,0,0,0.03));
      cursor: pointer;
      border: 1px dashed var(--ln-border, rgba(0,0,0,0.10));
      color: var(--ln-text-primary, #1E293B);
      font-size: 13px;
      font-weight: 600;
    }
  `;
  document.head.appendChild(s);
}

export class AskAweniFab {
  private ctx: AweniContext;
  private hostContainer: HTMLElement;
  private getLocation: () => LocationState;
  private fabEl: HTMLButtonElement | null = null;
  private overlayEl: HTMLDivElement | null = null;
  private sheetEl: HTMLDivElement | null = null;

  constructor(opts: AskAweniOptions) {
    this.ctx = opts.ctx;
    this.hostContainer = opts.hostContainer;
    this.getLocation = opts.getLocation;
  }

  mount(): void {
    injectStyles();
    if (this.fabEl) return; // already mounted
    const fab = document.createElement('button');
    fab.className = 'askfab-btn';
    fab.type = 'button';
    fab.setAttribute('aria-label', this.ctx.i18n.t('ask_fab_label') || 'Ask AWENI');
    fab.innerHTML = '✨';
    fab.addEventListener('click', () => this.openSheet());
    document.body.appendChild(fab);
    this.fabEl = fab;
  }

  unmount(): void {
    this.closeSheet();
    if (this.fabEl) {
      this.fabEl.remove();
      this.fabEl = null;
    }
  }

  private openSheet(): void {
    if (this.sheetEl) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const isRtl = this.ctx.i18n.dir === 'rtl';

    const overlay = document.createElement('div');
    overlay.className = 'askfab-overlay';
    overlay.addEventListener('click', () => this.closeSheet());
    document.body.appendChild(overlay);
    this.overlayEl = overlay;

    const sheet = document.createElement('div');
    sheet.className = 'askfab-sheet';
    sheet.setAttribute('dir', isRtl ? 'rtl' : 'ltr');

    const presets = this.pickPresets();

    sheet.innerHTML = `
      <div class="askfab-grab"></div>
      <div class="askfab-title">${escapeHtml(t('ask_sheet_title') || 'What can I help with?')}</div>
      <div class="askfab-sub">${escapeHtml(t('ask_sheet_sub') || 'Tap a card — no typing required.')}</div>
      <div class="askfab-grid">
        ${presets.map((p) => `
          <button type="button" class="askfab-tile" data-preset="${p.id}" data-prompt="${escapeAttr(t(p.promptKey) || p.id)}">
            <span class="askfab-tile-icon">${p.icon}</span>
            <span>${escapeHtml(t(p.labelKey) || p.id)}</span>
          </button>
        `).join('')}
      </div>
      <button type="button" class="askfab-custom" data-act="custom">
        <span style="font-size:18px;">⌨</span>
        <span>${escapeHtml(t('ask_sheet_custom') || 'Type your own message…')}</span>
      </button>
    `;

    sheet.querySelectorAll<HTMLButtonElement>('[data-preset]').forEach((b) => {
      b.addEventListener('click', () => {
        const prompt = b.dataset.prompt || '';
        this.dispatchToChat(prompt);
        this.closeSheet();
      });
    });

    sheet.querySelector('[data-act="custom"]')?.addEventListener('click', () => {
      this.closeSheet();
      // Just navigate to chat with no preset; user types there.
      this.ctx.navigate('chat');
    });

    document.body.appendChild(sheet);
    this.sheetEl = sheet;

    // ESC closes
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') this.closeSheet();
    };
    document.addEventListener('keydown', onKey);
    sheet.dataset.keyHandler = 'attached';
    // Detach key on close
    const observer = new MutationObserver(() => {
      if (!document.contains(sheet)) {
        document.removeEventListener('keydown', onKey);
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true });
  }

  private closeSheet(): void {
    if (this.sheetEl) { this.sheetEl.remove(); this.sheetEl = null; }
    if (this.overlayEl) { this.overlayEl.remove(); this.overlayEl = null; }
  }

  /** Pick the 6 most relevant presets given current context. */
  private pickPresets(): PresetPrompt[] {
    const now = new Date();
    const hour = now.getHours();
    const isFriday = now.getDay() === 5;
    const loc = this.getLocation();
    const isTraveling = !!loc.traveling;

    // Filter: keep all that have no rule, or whose rule passes.
    const eligible = ALL_PRESETS.filter((p) => !p.showIf || p.showIf({ hour, isFriday, isTraveling }));
    // Stable hash by date so the user sees the same set during a day,
    // not a fresh shuffle on every tap.
    const seed = `${now.getFullYear()}-${now.getMonth()}-${now.getDate()}`;
    const hash = (s: string): number => {
      let h = 0;
      for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
      return Math.abs(h);
    };
    // Sort by stable hash for deterministic order; keep top 6.
    const sorted = [...eligible].sort((a, b) => hash(a.id + seed) - hash(b.id + seed));
    return sorted.slice(0, 6);
  }

  private dispatchToChat(prompt: string): void {
    if (!prompt.trim()) {
      this.ctx.navigate('chat');
      return;
    }
    // Persist intent for ChatModule to pick up on mount.
    try {
      sessionStorage.setItem('aweni_chat_pending_prompt', prompt);
    } catch { /* ignore */ }
    this.ctx.navigate('chat');
  }
}

function escapeHtml(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escapeAttr(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
